import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import ClickableJapaneseText from '../japanese/ClickableJapaneseText';
import { isJapanese } from '../japanese/japaneseUtils';

const Clipboard = () => {
  const [clipboardText, setClipboardText] = useState<string>('');
  const [translatedText, setTranslatedText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handlePaste = async () => {
    try {
      let text = await navigator.clipboard.readText();
      if (isJapanese(text)) {
        text = text.replace(/\s+/g, '');
      }
      setClipboardText(text);
      setTranslatedText('');
    } catch (error) {
      console.error('Failed to read clipboard contents:', error);
      setClipboardText('Error reading clipboard');
    }
  };

  const handlePasteAppend = async () => {
    try {
      let text = await navigator.clipboard.readText();
      if (isJapanese(text)) {
        text = text.replace(/\s+/g, '');
      }
      setClipboardText(prev => prev ? `${prev}\n${text}` : text);
      setTranslatedText('');
    } catch (error) {
      console.error('Failed to read clipboard contents:', error);
    }
  };

  const handleTranslate = async () => {
    if (!clipboardText) return;
    setLoading(true);
    try {
      const response = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=en&dt=t&q=${encodeURIComponent(clipboardText)}`
      );
      const data = await response.json();
      const translation = data[0]?.map((item: any) => item[0]).join('') || '';
      setTranslatedText(translation);
    } catch (error) {
      console.error('Failed to translate text:', error);
      setTranslatedText('Error translating text');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Clipboard Reader</title>
        <meta name="description" content="Read and translate clipboard contents" />
      </Helmet>
      <div className="fixed h-full w-full bg-[#09090b] flex flex-col items-center justify-center gap-10">
        <Link to="/" className="absolute top-8 left-8 text-zinc-600 hover:text-zinc-400 text-sm transition-colors duration-150">← back</Link>

        <p className="text-zinc-600 text-xs tracking-widest uppercase">clipboard reader</p>

        <div className="max-h-[35vh] overflow-y-auto max-w-screen-md w-full px-8">
          {isJapanese(clipboardText) ? (
            <p className="text-zinc-100 text-2xl text-center leading-relaxed">
              <ClickableJapaneseText text={clipboardText} />
            </p>
          ) : (
            <p className="text-zinc-700 text-lg text-center">
              {clipboardText || 'paste something'}
            </p>
          )}
        </div>

        {loading ? (
          <div className="w-5 h-5 border-2 border-t-zinc-400 border-zinc-700 rounded-full animate-spin" />
        ) : (
          translatedText && (
            <div className="max-h-[25vh] overflow-y-auto max-w-screen-md w-full px-8">
              <p className="text-zinc-500 text-base text-center whitespace-pre-line">
                {translatedText}
              </p>
            </div>
          )
        )}

        <div className="flex gap-3">
          <button
            onClick={handlePaste}
            className="px-5 py-2.5 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm rounded-lg transition-colors duration-150"
          >
            paste
          </button>
          <button
            onClick={handlePasteAppend}
            className="px-5 py-2.5 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm rounded-lg transition-colors duration-150"
          >
            paste append
          </button>
          <button
            onClick={handleTranslate}
            className="px-5 py-2.5 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm rounded-lg transition-colors duration-150"
          >
            translate
          </button>
        </div>
      </div>
    </div>
  );
};

export default Clipboard;
